// WishEdit — `/wishes/:id/edit` route (W49.0 §8).
//
// **Why this page exists.** `patchWish` had five call sites and every one of
// them changed `status` and nothing else, so a person could mark a wish done
// but could not correct its name, move its date, change its priority or make it
// private. The mobile sheet offered 「編輯」 as a `disabled` row whose hint read
// 「W33.3 上線」 — accurate only in the sense that it never did. The fields
// already existed and already worked; they lived inside `/wishes/new` and had no
// second caller, which is the whole reason there was nothing to edit with.
//
// **A separate page, not inline editing in the sheet.** Five fields in a 90vh
// sheet is cramped the moment the keyboard opens, and the app already has six
// create/edit pages with this exact shape — `/wishes/:id/edit-layout` is the
// closest sibling and differs only in which fields it shows. mobile-first does
// not mean putting everything into a sheet; high-frequency changes (status) stay
// in the sheet as an instant PATCH, low-frequency ones (content) get a page.
//
// Layout follows `/wishes/new` exactly — same hero treatment (W49.0 §6), same
// `pb-40` clearance for the docked bar (W49.0 §7), same `SectionStack` with no
// card frame. The two pages sharing a shape is the point; if they drift, the
// guard test in `dockedBarClearance.test.ts` is what catches the half that
// breaks the layout.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { SectionStack } from "@/components/layout/SectionStack";
import { PageHero } from "@/components/primitives/PageHero";
import { PageErrorBoundary } from "@/components/ErrorBoundary";
import { PageBottomActionBar } from "@/components/system/PageBottomActionBar";
import { useHidePillNav } from "@/components/system/BottomChrome";
import { LoadingState } from "@/components/primitives/LoadingState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { useIsMobile } from "@/lib/hooks/useIsMobile";
import { toHktToday } from "@/lib/utils/hkt-time";
import { getWish, patchWish, type WishOut } from "@/lib/api/wishlist";
import { queryKeys } from "@/lib/queries/keys";
import { ApiError } from "@/lib/api/pair";
import { WishFormFields, type WishFormState } from "./wishes/WishFormFields";

const WISH_QUERY_KEY_PREFIX = ["wishes", "detail"] as const;

function fromWish(wish: WishOut): WishFormState {
  return {
    name: wish.name,
    category: wish.category,
    priority: wish.priority,
    targetDate: wish.target_date ?? "",
    visibility: wish.visibility,
  };
}

export function WishEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const wishId = id ?? "";
  useHidePillNav();
  const formRef = useRef<HTMLFormElement>(null);

  const [form, setForm] = useState<WishFormState | null>(null);
  const [touched, setTouched] = useState(false);
  const today = useMemo(() => toHktToday(), []);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [...WISH_QUERY_KEY_PREFIX, wishId],
    queryFn: () => getWish(wishId),
    enabled: Boolean(wishId),
    staleTime: 30_000,
  });

  // Seed the form once the wish arrives. Guarded on `form === null` so a
  // background refetch does not overwrite what someone is halfway through
  // typing — the same "a draft outranks the fetched copy" rule the block panel
  // follows.
  useEffect(() => {
    if (data && form === null) setForm(fromWish(data));
  }, [data, form]);

  const saveMut = useMutation({
    mutationFn: (payload: WishFormState) =>
      patchWish(wishId, {
        name: payload.name.trim(),
        category: payload.category,
        priority: payload.priority,
        target_date: payload.targetDate ? payload.targetDate : null,
        visibility: payload.visibility,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...WISH_QUERY_KEY_PREFIX, wishId] });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "list"] });
      void queryClient.invalidateQueries({ queryKey: ["wishes", "stats"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      navigate(`/wishes/${wishId}`, { replace: true });
    },
  });

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setTouched(true);
      if (!form) return;
      if (!form.name.trim()) return;
      saveMut.mutate(form);
    },
    [form, saveMut],
  );

  const nameInvalid = touched && !form?.name.trim();
  const dateInvalid = Boolean(
    touched && form && form.targetDate.length > 0 && form.targetDate < today,
  );
  const errorMessage =
    saveMut.error instanceof ApiError
      ? saveMut.error.message
      : saveMut.error instanceof Error
        ? saveMut.error.message
        : null;

  const hero = (
    <PageHero
      title="編輯心願"
      eyebrow="修改"
      subtitle="名稱 · 類別 · 優先度 · 日期 · 可見性"
      trailing={
        <button
          type="button"
          onClick={() => navigate(`/wishes/${wishId}`)}
          aria-label="返回心願詳情"
          data-testid="wish-edit-back"
          className="press inline-flex h-11 w-11 items-center justify-center rounded-full text-ink-2 hover:bg-black/5"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
        </button>
      }
    />
  );

  const fields = form ? (
    <WishFormFields
      form={form}
      onChange={(patch) => setForm((f) => (f ? { ...f, ...patch } : f))}
      nameInvalid={Boolean(nameInvalid)}
      dateInvalid={dateInvalid}
      today={today}
      errorMessage={errorMessage}
    />
  ) : null;

  const onSubmitClick = () => formRef.current?.requestSubmit();

  const body =
    isLoading || !form ? (
      <LoadingState shape="card" count={3} />
    ) : isError ? (
      <ErrorState
        title="讀取心願失敗"
        description={error instanceof Error ? error.message : "請稍後再試"}
        onRetry={() => refetch()}
      />
    ) : (
      <form
        ref={formRef}
        data-component="WishEditForm"
        data-testid="wish-edit-form"
        onSubmit={onSubmit}
        className="flex flex-col gap-s-4"
        noValidate
      >
        {fields}
      </form>
    );

  const saveDisabled = saveMut.isPending || !form?.name.trim();

  return (
    <PageErrorBoundary label="編輯心願">
      <div className="flex flex-col pb-40" data-testid="wish-edit-page">
        {hero}
        <PageContainer vertical="loose">
          <SectionStack density="default">{body}</SectionStack>
          {isMobile ? (
            <PageBottomActionBar
              primary={{
                label: saveMut.isPending ? "儲存中…" : "儲存",
                onClick: onSubmitClick,
                disabled: saveDisabled,
                loading: saveMut.isPending,
                leadingIcon: <Check size={18} aria-hidden />,
                testId: "wish-edit-submit",
              }}
              back={{
                label: "返回心願詳情",
                onClick: () => navigate(`/wishes/${wishId}`),
                testId: "wish-edit-bar-back",
              }}
              testId="wish-edit-bottom-bar"
            />
          ) : (
            <div
              className="flex items-center justify-end gap-s-2 pt-s-2 border-t border-hairline"
              data-component="WishEditFormActions"
            >
              <button
                type="button"
                onClick={() => navigate(`/wishes/${wishId}`)}
                data-testid="wish-edit-cancel"
                className="press inline-flex items-center min-h-11 px-s-4 rounded-full border border-hairline text-ink-1"
              >
                取消
              </button>
              <button
                type="button"
                onClick={onSubmitClick}
                disabled={saveDisabled}
                data-testid="wish-edit-submit"
                className="press inline-flex items-center gap-1.5 min-h-11 px-s-4 rounded-full bg-ink-1 text-paper text-body-sm font-semibold disabled:opacity-50"
              >
                <Check className="h-4 w-4" aria-hidden />
                {saveMut.isPending ? "儲存中…" : "儲存"}
              </button>
            </div>
          )}
        </PageContainer>
      </div>
    </PageErrorBoundary>
  );
}
